<template>
  <SectionWrapper :class="editMode ? 'editmode' : 'viewmode'">
    <Fieldset :legend="t('labels.contractsHeader')">
      <div class="flex">
        <FormInputText
          v-model="name"
          name="name"
          :isDirty="handleDirtyState('name')"
          :initialValue="editData?.name"
          :disabled="!editMode"
        />
        <FormSelect
          v-model="contractTypeId"
          name="contractTypeId"
          label="contractType"
          :options="contractTypes"
          :isDirty="handleDirtyState('contractTypeId')"
          :initialValue="editData?.contractTypeId"
          :disabled="!editMode || !isNew"
          :viewMode="!editMode || !isNew"
          filter
        />
      </div>
    </Fieldset>
    <Fieldset :legend="t('labels.contractsPartiesTaker')">
      <div class="flex">
        <!-- TODO: use later when more than one customerRole
             <FormSelect
            name="customerRole"
            label="role"
            :options="Object.values([ContractingPartyToContractDto.role.PROPERTY_TENANT])"
            v-model="customerRole"
            :isDirty="handleDirtyState('customerRole')"
            :initialValue="editData?.customerRole"
            :disabled="!editMode"
            singleValueFixed
            autofillSingleValue
          /> -->
        <FormSelect
          key="customerGroupId"
          v-model="customerGroupId"
          name="customerGroupId"
          label="customerGroup"
          :options="customerGroups"
          :isDirty="handleDirtyState('customerGroupId')"
          :initialValue="customerGroupId"
          :disabled="isDisabled('customerGroupId')"
          :viewMode="isDisabled('customerGroupId')"
          untranslated
          singleValueFixed
          autofillSingleValue
        />
        <FormSelect
          key="customerId"
          v-model="customerId"
          name="customerId"
          label="customer"
          :options="filteredCustomers"
          :isDirty="handleDirtyState('customerId')"
          :initialValue="editData?.customerId"
          :disabled="isDisabled('customerId')"
          :viewMode="isDisabled('customerId')"
          untranslated
          singleValueFixed
          autofillSingleValue
        />
      </div>
    </Fieldset>
    <Fieldset :legend="t('labels.contractsDates')">
      <div class="flex">
        <FormDatePicker
          v-model="startDate"
          name="startDate"
          :maxDate="endDate"
          :isDirty="handleDirtyState('startDate')"
          :initialValue="editData?.startDate"
          :disabled="!editMode"
        />
        <FormDatePicker
          v-model="endDate"
          name="endDate"
          :minDate="startDate"
          :isDirty="handleDirtyState('endDate')"
          :initialValue="editData?.endDate"
          :disabled="!editMode"
        />
        <FormDatePicker
          v-model="handOverDate"
          name="handOverDate"
          :isDirty="handleDirtyState('handOverDate')"
          :initialValue="editData?.handOverDate"
          :disabled="!editMode"
        />
      </div>
    </Fieldset>
  </SectionWrapper>
  <ContractAreas
    :areas="areas"
    :isNew="props.isNew"
    :data="props.editData"
    :customerId="customerId ?? undefined"
    :editMode="props.editMode"
    :fetchData="fetchData"
    :handleValidateAreas="handleValidateAreas"
  />
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';
import { useField } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type {
  DetailFormDisableProps,
  DetailFormExtraProps,
} from '@/components/details/detailTypes';
import type { SelectModel } from '@/components/forms/FormSelect.vue';
import { useAreas } from '@/views/areas/hooks/useAreas';
import { toSelectModel } from '@/utils/conversion';

import type { ContractBaseDataProps, ContractSimple } from '../contractsTypes';

import ContractAreas from './ContractAreas.vue';

type ContractInitialData = Partial<ContractSimple>;

const props = withDefaults(
  defineProps<
    ContractBaseDataProps &
      DetailFormExtraProps<ContractSimple> &
      DetailFormDisableProps<ContractSimple>
  >(),
  {
    initialData: undefined,
    placeholders: undefined,
    allDisabled: undefined,
    disabledFields: undefined,
    suggestions: undefined,
  },
);

const { t } = useI18n();
const { fetchAreasByCustomerId } = useAreas();

const areas = ref<SelectModel[]>([]);
const filteredCustomers = ref<SelectModel[]>([]);

const { value: name } = useField<string>('name');
const { value: contractTypeId } = useField<string>('contractTypeId');
const { value: customerGroupId } = useField<string | null>('customerGroupId');
const { value: customerId } = useField<string | null>('customerId');
// const { value: customerRole } = useField<string>('customerRole');
const { value: startDate } = useField<string>('startDate');
const { value: endDate } = useField<string | undefined>('endDate');
const { value: handOverDate } = useField<string | undefined>('handOverDate');

const isDisabled = (key?: keyof ContractSimple): boolean => {
  if (props.allDisabled === true) return true;

  // locally narrow disabledFields to the correct key type
  const disabledMap = props.disabledFields;

  if (key && disabledMap && (disabledMap as Record<string, boolean>)[key] === true) return true;

  return !props.editMode;
};

watch(
  () => [props.customers],
  () => {
    filteredCustomers.value = toSelectModel(props.customers);
  },
  { immediate: true, deep: true },
);

watch([customerGroupId], ([newId]) => {
  if (newId !== undefined && props.customers.length > 0) {
    filteredCustomers.value = toSelectModel(
      props.customers.filter((c) => c.customerGroupId === newId),
    );

    if (!filteredCustomers.value.map((x) => x.id).includes(customerId.value ?? '')) {
      customerId.value = '';
    }
  }
});

watch(
  () => customerId.value,
  async (newId) => {
    if (newId) {
      areas.value = toSelectModel((await fetchAreasByCustomerId(newId)) ?? []);
      return;
    }

    areas.value = [];
  },
);

watch(
  () => props.editMode,
  async () => {
    if (customerId.value) {
      areas.value = toSelectModel((await fetchAreasByCustomerId(customerId.value)) ?? []);
    }
  },
  { immediate: true },
);

/**
 * fills initial data when given by props
 */
watch(
  () => props.initialData as ContractInitialData | undefined,
  (init) => {
    if (!init) return;
    if (props.isNew) {
      if (!customerGroupId.value && init.customerGroupId) {
        customerGroupId.value = init.customerGroupId;
      }

      if (!customerId.value && init.customerId) {
        customerId.value = init.customerId;
      }
    }
  },
  { immediate: true, deep: true },
);
</script>
